import React, { useEffect } from "react";
import { connect } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import _ from "lodash";
import { MENU_GUARD_URLS } from "../../_utils/constants/menuOrder";
import { USER_MENU_LIST_REQUEST } from "../../_utils/constants/admin/Admin";

// Normalize a url for comparison: trim, drop any trailing slash and lowercase so
// casing / trailing-slash differences between the route path and the api menuUrl
// never matter.
const normalize = (url = "") => url.trim().replace(/\/+$/, "").toLowerCase();

// Sub-menu urls arrive from the api in two forms: already absolute ("/role") or
// relative to the admin section ("labor/catgeory"). Resolve both to the real
// route path before comparing.
const toRoutePath = (url = "") => {
  const trimmed = (url || "").trim();
  if (!trimmed) return "";
  return normalize(trimmed.startsWith("/") ? trimmed : `/admin/${trimmed}`);
};

// The dashboard is the universal landing page and the redirect target, so it is
// always accessible and must never be policed (prevents a redirect loop).
const DASHBOARD_URL = normalize("/dashboard");

// The only routes this guard is responsible for. Everything else passes through.
const GUARDED_MENU_SET = new Set(MENU_GUARD_URLS.map(normalize));

// The permission tree comes back in two shapes: a flat array of menus, or a
// single wrapper object exposing them under `.permission`. Support both.
const getMenus = (userMenuList) => {
  if (_.isEmpty(userMenuList)) return [];
  if (Array.isArray(userMenuList[0]?.permission)) return userMenuList[0].permission;
  return userMenuList;
};

// Flatten the permission tree into the set of urls the user may open, covering
// both menus and their sub-menus (status === true only).
const getAllowedUrls = (menus) => {
  const urls = new Set();
  menus.forEach((menu) => {
    if (!menu?.status) return;
    if (menu?.menuUrl) urls.add(normalize(menu.menuUrl));
    (menu?.subMenu || []).forEach((sub) => {
      if (sub?.subMenuUrl) urls.add(toRoutePath(sub.subMenuUrl));
    });
  });
  return urls;
};

// Restricts direct-URL access to menu / sub-menu pages the logged-in user is not
// permitted to see. The allowed list is derived entirely from the role's
// permission response — no per-route hardcoding. Any route that is not a guarded
// menu destination is left completely untouched.
const MenuPermissionGuard = ({
  auth: { auth, isAuthenticated },
  Admin: { userMenuList },
  fetchUserMenuList,
}) => {
  const location = useLocation();
  const currentPath = normalize(location.pathname);

  // Safety net: make sure the permission list is loaded even if the Sidebar
  // that normally fetches it has not mounted yet on a direct URL hit.
  useEffect(() => {
    if (
      isAuthenticated &&
      _.isEmpty(userMenuList) &&
      auth[0]?.token &&
      auth[0]?.roleid &&
      auth[0]?.userId
    ) {
      fetchUserMenuList(auth[0].token, auth[0].roleid, auth[0].userId);
    }
  }, [isAuthenticated, userMenuList, auth]);

  if (!isAuthenticated) {
    return null;
  }

  // Never police the dashboard, and ignore every route that is not a guarded
  // menu destination (detail pages, value-lists, sub-pages, etc.).
  if (currentPath === DASHBOARD_URL || !GUARDED_MENU_SET.has(currentPath)) {
    return null;
  }

  // Wait until permissions are available before deciding anything.
  const menus = getMenus(userMenuList);
  if (_.isEmpty(menus)) {
    return null;
  }

  // A guarded menu page the user is not permitted to open -> back to dashboard.
  const allowedUrls = getAllowedUrls(menus);
  if (!allowedUrls.has(currentPath)) {
    return <Navigate to="/dashboard" replace />;
  }

  return null;
};

const mapStateToProps = ({ auth, Admin }) => ({ auth, Admin });

const mapDispatchToProps = (dispatch) => ({
  fetchUserMenuList: (token, roleId, userId) =>
    dispatch({ type: USER_MENU_LIST_REQUEST, token, roleId, userId }),
});

export default connect(mapStateToProps, mapDispatchToProps)(MenuPermissionGuard);
